import type { PaginationState } from "@tanstack/react-table"
import { t } from "@lingui/core/macro"
import {
  useMutation,
  keepPreviousData,
  queryOptions,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { RefreshCcw, Search } from "lucide-react"
import { useMemo, useState } from "react"
import { toast } from "sonner"
import { z } from "zod"
import type {
  ShareFilters,
  ShareListResponse,
  ShareWithDetails,
} from "@/features/store-files/core/types/share"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { DataTable2 } from "@/components/ui/datatable2"
import { Input } from "@/components/ui/input"
import { PageHeader } from "@/components/ui/PageHeader"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { createSharesColumns, RevokeShareDialog } from "@/features/store-files/components"
import { listAllShares, revokeFileShare } from "@/features/store-files/server/fileServer"
import { usePermissions } from "@/hooks/usePermissions"
import { useLanguage } from "@/i18n/i18n"
import { fileSharesQueryKeys } from "@/lib/queryKeys"
import { cleanEmptyParams } from "@/lib/url-utils"

// Zod schema for search params validation
const shareFiltersSchema = z.object({
  status: z.enum(["active", "expired", "revoked", "all"]).optional(),
  search: z.string().optional(),
  page: z.number().optional(),
  pageSize: z.number().optional(),
  sort: z.enum(["createdAt", "expiresAt", "fileName"]).optional(),
  order: z.enum(["asc", "desc"]).optional(),
  showAll: z.boolean().optional(),
})

type ShareFiltersSearch = z.infer<typeof shareFiltersSchema>

const DEFAULT_PAGE_SIZE = 25

const sharesQuery = (filters: ShareFilters) =>
  queryOptions<ShareListResponse>({
    queryKey: fileSharesQueryKeys.list(filters as Record<string, unknown>),
    queryFn: () => listAllShares({ data: filters }),
    placeholderData: keepPreviousData,
  })

const ALL_STATUS_VALUE = "__all__"

export const Route = createFileRoute("/(authenticated)/settings/shares/")({
  validateSearch: shareFiltersSchema,
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    void context.queryClient.ensureQueryData(sharesQuery(deps))
  },
  component: SharesSettingsPage,
})

function SharesSettingsPage() {
  const filters = Route.useSearch()
  const navigate = Route.useNavigate()
  const [language] = useLanguage()
  const { isAdmin, hasPermission } = usePermissions()
  const queryClient = useQueryClient()

  // Check if user can see all shares (admin or blueprint_planner)
  const canSeeAllShares = isAdmin || hasPermission("configVersion", "update").allowed

  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false)
  const [shareToRevoke, setShareToRevoke] = useState<ShareWithDetails | null>(null)
  const [localSearchValue, setLocalSearchValue] = useState(filters.search ?? "")

  const { data, isFetching } = useQuery(sharesQuery(filters))

  const revokeMutation = useMutation({
    mutationFn: (shareId: string) => revokeFileShare({ data: shareId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: fileSharesQueryKeys.all })
      toast.success(t`Share revoked`)
      setRevokeDialogOpen(false)
      setShareToRevoke(null)
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  const setFilters = (partialFilters: Partial<ShareFiltersSearch>) => {
    void navigate({
      search: (prev: ShareFiltersSearch) => cleanEmptyParams({ ...prev, ...partialFilters }),
    })
  }

  const resetFilters = () => {
    void navigate({ search: {} })
    setLocalSearchValue("")
  }

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setFilters({ search: localSearchValue || undefined, page: 1 })
    }
  }

  const handleCopyLink = (share: ShareWithDetails) => {
    const url = `${window.location.origin}/shared/${share.token}`
    void navigator.clipboard.writeText(url)
    toast.success(t`Link copied to clipboard`)
  }

  const handleOpenLink = (share: ShareWithDetails) => {
    const url = `${window.location.origin}/shared/${share.token}`
    window.open(url, "_blank")
  }

  const handleRevokeShare = (share: ShareWithDetails) => {
    setShareToRevoke(share)
    setRevokeDialogOpen(true)
  }

  const confirmRevoke = () => {
    if (shareToRevoke) {
      revokeMutation.mutate(shareToRevoke.id)
    }
  }

  const paginationState: PaginationState = {
    pageIndex: (filters.page ?? 1) - 1,
    pageSize: filters.pageSize ?? DEFAULT_PAGE_SIZE,
  }

  const columns = useMemo(
    () =>
      createSharesColumns({
        language,
        showCreatorColumn: canSeeAllShares && (filters.showAll ?? false),
        onCopyLink: handleCopyLink,
        onOpenLink: handleOpenLink,
        onRevokeShare: handleRevokeShare,
      }),
    [language, canSeeAllShares, filters.showAll],
  )

  return (
    <div className="flex-1 space-y-6 overflow-y-auto pb-8">
      <PageHeader
        title={t`Shared Links`}
        description={t`Manage file share links. You can revoke links to prevent further access.`}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={resetFilters} className="gap-2">
              <RefreshCcw className="h-4 w-4" />
              {t`Reset filters`}
            </Button>
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-4">
        {/* Search */}
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
          <Input
            placeholder={t`Search by file name...`}
            value={localSearchValue}
            onChange={(e) => setLocalSearchValue(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            className="pl-9"
          />
        </div>

        {/* Status filter */}
        <Select
          value={filters.status || ALL_STATUS_VALUE}
          onValueChange={(value) =>
            setFilters({
              status: value === ALL_STATUS_VALUE ? undefined : (value as ShareFilters["status"]),
              page: 1,
            })
          }
        >
          <SelectTrigger className="w-36">
            <SelectValue placeholder={t`Status`} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_STATUS_VALUE}>{t`All statuses`}</SelectItem>
            <SelectItem value="active">{t`Active`}</SelectItem>
            <SelectItem value="expired">{t`Expired`}</SelectItem>
            <SelectItem value="revoked">{t`Revoked`}</SelectItem>
          </SelectContent>
        </Select>

        {/* Show all users toggle (admin/blueprint_planner only) */}
        {canSeeAllShares && (
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={filters.showAll ?? false}
              onCheckedChange={(checked) =>
                setFilters({ showAll: checked === true ? true : undefined, page: 1 })
              }
            />
            <span className="text-neutral-700 dark:text-neutral-300">{t`Show all users' shares`}</span>
          </label>
        )}
      </div>

      <div className="border-neutral-150 overflow-hidden rounded-lg border bg-white shadow-sm dark:border-neutral-700 dark:bg-neutral-900/50">
        <DataTable2
          columns={columns}
          data={data?.shares ?? []}
          rowCount={data?.total ?? 0}
          enableSorting
          sortingState={filters.sort ? [{ id: filters.sort, desc: filters.order === "desc" }] : []}
          onSortingChange={(sorting) => {
            if (sorting.length > 0) {
              setFilters({
                sort: sorting[0].id as ShareFilters["sort"],
                order: sorting[0].desc ? "desc" : "asc",
              })
            } else {
              setFilters({ sort: undefined, order: undefined })
            }
          }}
          paginationState={paginationState}
          onPaginationChange={(newPagination) =>
            setFilters({
              page: newPagination.pageIndex + 1,
              pageSize: newPagination.pageSize,
            })
          }
          enablePagination
          isFetching={isFetching}
        />
      </div>

      <RevokeShareDialog
        open={revokeDialogOpen}
        onOpenChange={setRevokeDialogOpen}
        share={shareToRevoke}
        onConfirm={confirmRevoke}
        isPending={revokeMutation.isPending}
      />
    </div>
  )
}
